/**
 * Native bridge — abstracts Capacitor plugin calls with browser fallbacks.
 *
 * On the web bundle, the relevant Capacitor packages are not bundled; the
 * fallback paths are used. On the native shell, capacitor.config.json declares
 * the plugins and the bundle accesses them via `(window as any).Capacitor`.
 *
 * Keep this module dependency-free of @capacitor/* so the web build doesn't
 * pull native code.
 */

type Caps = {
  Plugins?: {
    Haptics?: {
      impact(opts: { style: 'LIGHT' | 'MEDIUM' | 'HEAVY' }): Promise<void>;
      notification(opts: { type: 'SUCCESS' | 'WARNING' | 'ERROR' }): Promise<void>;
      vibrate?: (opts: { duration: number }) => Promise<void>;
    };
    StatusBar?: {
      setStyle(opts: { style: 'DARK' | 'LIGHT' | 'DEFAULT' }): Promise<void>;
      setBackgroundColor(opts: { color: string }): Promise<void>;
    };
    LocalNotifications?: {
      schedule(opts: { notifications: NotifSchedule[] }): Promise<void>;
      requestPermissions(): Promise<{ display: 'granted' | 'denied' | 'prompt' }>;
      cancel(opts: { notifications: Array<{ id: number }> }): Promise<void>;
    };
    /** Provided by a custom Capacitor plugin we author in iOS/Android (see native/). */
    FudetoIAP?: {
      getProducts(): Promise<Product[]>;
      purchase(opts: { productId: string }): Promise<PurchaseResult>;
      restore(): Promise<PurchaseResult[]>;
    };
    /** Same — custom plugin for rewarded ads. */
    FudetoAds?: {
      load(opts: { unit: 'rewarded' }): Promise<{ ready: boolean }>;
      show(opts: { unit: 'rewarded' }): Promise<{
        granted: boolean;
        reward?: { amount: number; type: string };
      }>;
    };
  };
};

interface NotifSchedule {
  id: number;
  title: string;
  body: string;
  schedule?: { at?: Date; repeats?: boolean };
}

interface Product {
  id: string;
  title: string;
  price: string; // formatted string from store, locale-aware
  currency: string;
  amountMicros: number;
}

interface PurchaseResult {
  productId: string;
  receipt: string;
  status: 'completed' | 'pending' | 'failed';
}

function caps(): NonNullable<Caps['Plugins']> {
  return ((window as Window & { Capacitor?: Caps }).Capacitor?.Plugins ?? {}) as NonNullable<
    Caps['Plugins']
  >;
}

function isNative(): boolean {
  return Boolean(
    (
      window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }
    ).Capacitor?.isNativePlatform?.(),
  );
}

/** Haptic feedback. Maps to CoreHaptics (iOS) / Vibrator (Android) / vibrate API (web). */
export async function haptic(
  kind: 'light' | 'medium' | 'success' | 'warning' | 'error' = 'light',
): Promise<void> {
  const h = caps().Haptics;
  if (h) {
    try {
      if (kind === 'success' || kind === 'warning' || kind === 'error') {
        await h.notification({ type: kind.toUpperCase() as 'SUCCESS' | 'WARNING' | 'ERROR' });
      } else {
        await h.impact({ style: kind === 'medium' ? 'MEDIUM' : 'LIGHT' });
      }
      return;
    } catch {
      /* fall through */
    }
  }
  // Web fallback — Vibration API (Android Chrome only; no-op on iOS Safari).
  if ('vibrate' in navigator) {
    try {
      (navigator as Navigator & { vibrate: (ms: number | number[]) => void }).vibrate(
        kind === 'success' ? [10, 30, 10] : 12,
      );
    } catch {
      /* ignore */
    }
  }
}

/** Schedule a daily streak reminder at given local time (HH:MM). */
export async function scheduleStreakReminder(
  at: { hour: number; minute: number },
  title: string,
  body: string,
): Promise<boolean> {
  const ln = caps().LocalNotifications;
  if (!ln) return false;
  try {
    const perm = await ln.requestPermissions();
    if (perm.display !== 'granted') return false;
    const next = new Date();
    next.setHours(at.hour, at.minute, 0, 0);
    if (next.getTime() < Date.now()) next.setDate(next.getDate() + 1);
    await ln.schedule({
      notifications: [{ id: 1001, title, body, schedule: { at: next, repeats: true } }],
    });
    return true;
  } catch {
    return false;
  }
}

/** Cancel scheduled reminders. */
export async function cancelStreakReminder(): Promise<void> {
  const ln = caps().LocalNotifications;
  await ln?.cancel({ notifications: [{ id: 1001 }] });
}

/** Set native status-bar style to match active theme. No-op on web. */
export async function syncStatusBar(theme: 'light' | 'dark'): Promise<void> {
  const sb = caps().StatusBar;
  if (!sb || !isNative()) return;
  await sb.setStyle({ style: theme === 'dark' ? 'DARK' : 'LIGHT' });
  await sb.setBackgroundColor({ color: theme === 'dark' ? '#121212' : '#FAFAFA' });
}

/** Fetch product list from the store. Returns [] on web. */
export async function getProducts(): Promise<Product[]> {
  const iap = caps().FudetoIAP;
  if (!iap) return [];
  try {
    return await iap.getProducts();
  } catch {
    return [];
  }
}

/** Buy a product. Returns the receipt the worker will verify. */
export async function buyProduct(
  productId: 'unlimited' | 'pro_monthly' | 'pro_yearly',
): Promise<PurchaseResult | null> {
  const iap = caps().FudetoIAP;
  if (!iap) return null;
  try {
    return await iap.purchase({ productId });
  } catch {
    return null;
  }
}

/** Restore prior purchases (App Store / Google Play). */
export async function restorePurchases(): Promise<PurchaseResult[]> {
  const iap = caps().FudetoIAP;
  if (!iap) return [];
  try {
    return await iap.restore();
  } catch {
    return [];
  }
}

/** Show a rewarded ad. Returns true if the user completed it. */
export async function showRewardedAd(): Promise<{
  granted: boolean;
  reward?: { amount: number; type: string };
}> {
  const ads = caps().FudetoAds;
  if (!ads) return { granted: false };
  try {
    const ready = await ads.load({ unit: 'rewarded' });
    if (!ready.ready) return { granted: false };
    return await ads.show({ unit: 'rewarded' });
  } catch {
    return { granted: false };
  }
}

/** Best-effort detection — used by screens that hide native-only CTAs. */
export function hasNativeIAP(): boolean {
  return Boolean(caps().FudetoIAP);
}

export function hasNativeAds(): boolean {
  return Boolean(caps().FudetoAds);
}
